/**
 * 项目管理服务 - 项目相关 API
 * 路径与后端 /project/projects 对齐
 */

import api from '../../api';
import type { Result, PageResult } from '../../types';
import type { Project, ProjectMemberRow } from '../../../types';

export interface ProjectTraineeRow {
  traineeName: string;
  companyName: string;
  phone?: string;
  email?: string;
  department?: string;
  position?: string;
  tags?: string;
  sourceNote?: string;
}

export interface ProjectTraineeItem extends ProjectTraineeRow {
  id: string;
  projectId: string;
  contactId?: string;
  importedById?: string;
  importedByName?: string;
  createTime?: string;
  updateTime?: string;
}

export interface ProjectMilestoneItem {
  id: string;
  projectId: string;
  name: string;
  description?: string;
  dueDate: string;
  status: 'pending' | 'completed' | 'overdue' | 'in_progress';
  completedAt?: string;
  deliverables?: string;
  acceptanceStatus?: string;
  acceptanceNote?: string;
  acceptedById?: string;
  acceptedByName?: string;
  acceptedAt?: string;
  order?: number;
  createTime?: string;
  updateTime?: string;
}

export interface ProjectCloseoutReadiness {
  tasksReady: boolean;
  unfinishedTaskCount: number;
  totalStageCount: number;
  acceptedStageCount: number;
  milestonesReady: boolean;
  unfinishedMilestoneCount: number;
  hasProjectCloseoutReport: boolean;
  hasProjectCustomerAcceptance: boolean;
  hasTrainingCloseoutSummary: boolean;
  hasCloseoutNote: boolean;
  businessCloseoutSubmittable: boolean;
  financeCloseoutAvailable: boolean;
  financeCloseoutClosable: boolean;
  contractAmount: number;
  invoiceAmount: number;
  receivableAmount: number;
  receivedAmount: number;
  pendingReceivableAmount: number;
  payableAmount: number;
  paidPayableAmount: number;
  pendingPayableAmount: number;
  invoiceTaxAmount: number;
  internalLaborCost: number;
  expertFeeAmount: number;
  projectExpenseAmount: number;
  projectMonthlyExpenseAmount: number;
  totalProjectCost: number;
  actualGrossProfit: number;
  finalEstimatedProfit: number;
  memberInvestments: ProjectMemberInvestment[];
  blockingReasons: string[];
}

export interface ProjectMemberInvestment {
  userId: string;
  userName: string;
  source: 'employee' | 'expert' | string;
  projectRole?: string;
  taskCount: number;
  workDays: number;
  taskAmount: number;
  peopleCost: number;
  costType?: string;
  contributionNote?: string;
}

export interface ProjectMemberInvestmentRow extends ProjectMemberInvestment {
  configuredMemberId?: string;
  configuredStatus?: string;
}

const mergeProjectMembersByUserId = (
  projectMembers?: ProjectMemberRow[] | null,
): ProjectMemberRow[] => {
  const merged = new Map<string, ProjectMemberRow>();

  (projectMembers || []).forEach((member) => {
    if (!member.userId) return;
    const existing = merged.get(member.userId);
    if (!existing) {
      merged.set(member.userId, { ...member });
      return;
    }

    const roles = [existing.role, member.role].filter(Boolean);
    merged.set(member.userId, {
      ...existing,
      userName: existing.userName || member.userName,
      type: existing.type === 'expert' || member.type === 'expert' ? 'expert' : existing.type || member.type,
      role: Array.from(new Set(roles)).join(' / ') || existing.role || member.role,
      workDays: (existing.workDays ?? 0) + (member.workDays ?? 0),
      dailyRate: existing.dailyRate ?? member.dailyRate,
      totalCost: (existing.totalCost ?? 0) + (member.totalCost ?? 0),
      status: existing.status === 'active' || member.status === 'active' ? 'active' : existing.status || member.status,
    });
  });

  return Array.from(merged.values());
};

const normalizeMemberRole = (role?: string) => {
  if (role === 'manager') return '项目经理';
  if (role === 'member') return '项目成员';
  return role || '-';
};

const memberSourceFromType = (type?: string) => (type === 'expert' ? 'expert' : 'employee');

export const buildProjectMemberInvestmentRows = (
  projectMembers?: ProjectMemberRow[] | null,
  memberInvestments?: ProjectMemberInvestment[] | null,
): ProjectMemberInvestmentRow[] => {
  const investmentsByUserId = new Map(
    (memberInvestments || []).map((row) => [row.userId, row]),
  );
  const rows: ProjectMemberInvestmentRow[] = [];
  const seen = new Set<string>();

  mergeProjectMembersByUserId(projectMembers).forEach((member) => {
    const investment = investmentsByUserId.get(member.userId);
    seen.add(member.userId);
    rows.push({
      userId: member.userId,
      userName: investment?.userName || member.userName || member.userId,
      source: investment?.source || memberSourceFromType(member.type),
      projectRole: investment?.projectRole || normalizeMemberRole(member.role),
      taskCount: investment?.taskCount || 0,
      workDays: investment?.workDays ?? member.workDays ?? 0,
      taskAmount: investment?.taskAmount || 0,
      peopleCost: investment?.peopleCost ?? member.totalCost ?? 0,
      costType: investment?.costType,
      contributionNote: investment?.contributionNote || '-',
      configuredMemberId: member.id,
      configuredStatus: member.status,
    });
  });

  (memberInvestments || []).forEach((investment) => {
    if (seen.has(investment.userId)) return;
    rows.push(investment);
  });

  return rows;
};

export const getProjects = (params: Record<string, unknown>): Promise<Result<PageResult<Project>>> => {
  return api.get('/project/projects', { params });
};

export const getProjectById = (id: string): Promise<Result<Project>> => {
  return api.get(`/project/projects/${id}`);
};

export const createProject = (data: Record<string, unknown>): Promise<Result<string>> => {
  return api.post('/project/projects', data);
};

export const updateProject = (id: string, data: Record<string, unknown>): Promise<Result<void>> => {
  return api.put(`/project/projects/${id}`, data);
};

export const completeProject = (id: string): Promise<Result<void>> => {
  return api.put(`/project/projects/${id}/complete`);
};

export const getProjectCloseoutReadiness = (id: string): Promise<Result<ProjectCloseoutReadiness>> => {
  return api.get(`/project/projects/${id}/closeout-readiness`);
};

export const submitProjectBusinessCloseout = (
  id: string,
  data: { closeoutNote: string; attachmentIds?: string[]; customerAcceptanceAttachmentIds?: string[] },
): Promise<Result<void>> => {
  return api.post(`/project/projects/${id}/business-closeout/submit`, data);
};

export const confirmProjectFinanceCloseout = (
  id: string,
  data: { financeCloseNote: string },
): Promise<Result<void>> => {
  return api.post(`/project/projects/${id}/finance-closeout/confirm`, data);
};

export const deleteProject = (id: string): Promise<Result<void>> => {
  return api.delete(`/project/projects/${id}`);
};

/** 与后端 @RequestParam Integer progress 对齐，必须用 query，不可用 JSON body */
export const updateProjectProgress = (id: string, progress: number): Promise<Result<void>> => {
  return api.put(`/project/projects/${id}/progress`, null, { params: { progress } });
};

export const repairProjectSignArtifacts = (id: string): Promise<Result<string>> => {
  return api.post(`/project/projects/${id}/repair-sign-artifacts`);
};

/** 上传文件到服务器，返回可访问路径（如 /upload/...） */
export const uploadProjectBinary = (file: File, category = 'project-docs'): Promise<Result<string>> => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/system/files/upload', fd, {
    params: { category },
    timeout: 120000,
  });
};

/** 登记项目附件（合同 / 方案等），需先 uploadProjectBinary 拿到 url */
export const registerProjectAttachment = (
  projectId: string,
  params: {
    type: string;
    name: string;
    url: string;
    format: string;
    size: number;
    uploadById: string;
    uploadByName: string;
    milestoneId?: string;
    remark?: string;
  }
): Promise<Result<string>> => {
  return api.post(`/project/projects/${projectId}/attachments`, null, { params });
};

export const deleteProjectAttachment = (attachmentId: string): Promise<Result<void>> => {
  return api.delete(`/project/projects/attachments/${attachmentId}`);
};

export const getProjectMilestones = (projectId: string): Promise<Result<ProjectMilestoneItem[]>> => {
  return api.get(`/project/projects/${projectId}/milestones`);
};

export const createProjectMilestone = (
  projectId: string,
  data: Record<string, unknown>,
): Promise<Result<string>> => {
  return api.post(`/project/projects/${projectId}/milestones`, data);
};

export const updateProjectMilestone = (
  projectId: string,
  milestoneId: string,
  data: Record<string, unknown>,
): Promise<Result<void>> => {
  return api.put(`/project/projects/${projectId}/milestones/${milestoneId}`, data);
};

export const completeProjectMilestone = (
  projectId: string,
  milestoneId: string,
  data?: {
    acceptanceNote: string;
    acceptedById?: string;
    acceptedByName?: string;
  },
): Promise<Result<void>> => {
  return api.put(`/project/projects/${projectId}/milestones/${milestoneId}/complete`, data);
};

export const deleteProjectMilestone = (
  projectId: string,
  milestoneId: string,
): Promise<Result<void>> => {
  return api.delete(`/project/projects/${projectId}/milestones/${milestoneId}`);
};

export const getProjectTrainees = (projectId: string): Promise<Result<ProjectTraineeItem[]>> => {
  return api.get(`/project/projects/${projectId}/trainees`);
};

export const importProjectTrainees = (
  projectId: string,
  rows: ProjectTraineeRow[],
): Promise<Result<number>> => {
  return api.post(`/project/projects/${projectId}/trainees/import`, { rows });
};

export const deleteProjectTrainee = (projectId: string, traineeId: string): Promise<Result<void>> => {
  return api.delete(`/project/projects/${projectId}/trainees/${traineeId}`);
};
